
import SEO, { PAGE_SEO } from '../components/SEO';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, FileText } from 'lucide-react';

export default function TermsOfService() {
  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="terms-of-service-page">
      <SEO {...PAGE_SEO.terms} url="/terms" />
      <Navbar />
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Header */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold/10 rounded-sm mb-4">
              <FileText className="w-4 h-4 text-gold" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gold">Legal</span>
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl font-semibold mb-2">Terms of Service</h1>
            <p className="text-muted-foreground">Last updated: February 21, 2026</p>
          </div>

          {/* Content */}
          <Card className="bg-card border-none shadow-card">
            <CardContent className="p-8 md:p-12 prose prose-lg max-w-none">
              <section className="mb-8">
                <h2 className="font-serif text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
                <p className="text-muted-foreground leading-relaxed">
                  By accessing or using SkinUse ("the Service"), you agree to be bound by these Terms of Service 
                  ("Terms"). If you do not agree to these Terms, please do not use our Service. We reserve the 
                  right to modify these Terms at any time, and your continued use of the Service constitutes 
                  acceptance of any modifications.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-serif text-2xl font-semibold mb-4">2. Description of Service</h2>
                <p className="text-muted-foreground leading-relaxed">
                  SkinUse is a skin and hair care intelligence platform that provides personalized content, 
                  educational resources, quizzes, product information, and AI-powered beauty advice. Our Service 
                  includes curated articles, video tutorials, a gamification system (SkinPoints), and 
                  personalized recommendations.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-serif text-2xl font-semibold mb-4">3. User Accounts</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  To access certain features, you must create an account. You agree to:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>Provide accurate, current, and complete information</li>
                  <li>Maintain and update your information as needed</li>
                  <li>Keep your password secure and confidential</li>
                  <li>Accept responsibility for all activities under your account</li>
                  <li>Notify us immediately of any unauthorized use</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="font-serif text-2xl font-semibold mb-4">4. SkinPoints Program</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Our SkinPoints program rewards engagement with our platform:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>Points are earned by reading content, completing quizzes, and engaging with features</li>
                  <li>Points have no monetary value and cannot be exchanged for cash</li>
                  <li>We reserve the right to modify point values or the program at any time</li>
                  <li>Abuse of the system may result in point deduction or account suspension</li>
                  <li>Points are non-transferable between accounts</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="font-serif text-2xl font-semibold mb-4">5. Acceptable Use</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  You agree not to:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>Use the Service for any unlawful purpose</li>
                  <li>Attempt to gain unauthorized access to any part of the Service</li>
                  <li>Interfere with or disrupt the Service or servers</li>
                  <li>Upload malicious code or harmful content</li>
                  <li>Impersonate any person or entity</li>
                  <li>Harvest or collect user information without consent</li>
                  <li>Use automated systems to access the Service without permission</li>
                  <li>Engage in any activity that could damage our reputation</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="font-serif text-2xl font-semibold mb-4">6. Content and Intellectual Property</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  All content on SkinUse, including text, graphics, logos, images, videos, and software, is owned 
                  by or licensed to us and is protected by intellectual property laws. You may not:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>Copy, modify, or distribute our content without permission</li>
                  <li>Use our trademarks without written consent</li>
                  <li>Remove any copyright or proprietary notices</li>
                  <li>Create derivative works based on our content</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="font-serif text-2xl font-semibold mb-4">7. Medical Disclaimer</h2>
                <p className="text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Important:</strong> The content provided on SkinUse, including 
                  AI-powered advice, is for informational and educational purposes only. It is not intended to be 
                  a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of 
                  a qualified dermatologist or healthcare provider with any questions you may have regarding a 
                  medical condition. Never disregard professional medical advice or delay seeking it because of 
                  something you have read on our platform.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-serif text-2xl font-semibold mb-4">8. Third-Party Links and Products</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our Service may contain links to third-party websites and display product information from 
                  external sources (such as Open Beauty Facts). We are not responsible for the content, accuracy, 
                  or practices of these third parties. Any interactions with third-party sites or purchases of 
                  products are at your own risk.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-serif text-2xl font-semibold mb-4">9. Limitation of Liability</h2>
                <p className="text-muted-foreground leading-relaxed">
                  To the fullest extent permitted by law, SkinUse and its affiliates shall not be liable for any 
                  indirect, incidental, special, consequential, or punitive damages, including but not limited to 
                  loss of profits, data, or goodwill, arising out of or in connection with your use of the Service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-serif text-2xl font-semibold mb-4">10. Disclaimer of Warranties</h2>
                <p className="text-muted-foreground leading-relaxed">
                  The Service is provided "as is" and "as available" without warranties of any kind, either express 
                  or implied. We do not warrant that the Service will be uninterrupted, secure, or error-free, or 
                  that any defects will be corrected.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-serif text-2xl font-semibold mb-4">11. Account Termination</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We reserve the right to suspend or terminate your account at any time for violation of these 
                  Terms or for any other reason at our discretion. You may also delete your account at any time 
                  through your profile settings. Upon termination, your right to use the Service will immediately 
                  cease, and any accumulated SkinPoints will be forfeited.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-serif text-2xl font-semibold mb-4">12. Governing Law</h2>
                <p className="text-muted-foreground leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of the jurisdiction 
                  in which SkinUse operates, without regard to its conflict of law provisions.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl font-semibold mb-4">13. Contact Information</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <div className="mt-4 p-4 bg-secondary rounded-sm">
                  <p className="text-foreground font-medium">SkinUse Legal</p>
                  <p className="text-muted-foreground">Email: legal@skinuse.com</p>
                </div>
              </section>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
}
